import {Module, VuexModule, MutationAction, getModule, Mutation, Action } from 'vuex-module-decorators';
import {
    Cart,
    Totals,
    CartItem,
    UpdateCartRequest,
    QuoteAddress,
    CartResponse,
    AddToCartRequest,
    ConfigurableItemOption,
} from '../models';
import {
    getCart,
    getTotals,
    updateCartQty,
    deleteCartItem,
    addItemToWishlist,
    addToCart,
} from '../api';
import store from '@/store';
import { initState, blankState } from '@/store/stateinit';
import { debounce } from '@/commons/debounce';
import checkout from './checkout';
import { bus } from '@/events/bus';
import { event as receiveEvent } from '@/events/cartReceived';
// tutorial: helper per la sintassy async-await per evitare di dover wrappare l'await con try-catch
import to from 'await-to-js';
import handleError from '@/commons/errorHandler';

const loadingFlags = {
    totals: false,
    shipMethods: false,
    cap: false,
    yandex: false,
    hipay: false,
    paymentselection: false,
    canplaceOrder: true,
    guestlogin: false,
    guestregister: false,
    registrationlogin: false,
    processingpayment: false,
};

@Module({
    namespaced: true,
    name: 'cart',
    store,
    dynamic: true,
})
class CartModule extends VuexModule {
    public cart: Cart = initState('cart'); // tutorial: le proprietà di classe sono le proprietà dello state di Vuex
    public totals: Totals = initState('totals');
    public cartToken: string = initState('cartToken');
    public loadingFlags: any = loadingFlags;

    get token(): string { // tutorial: metodi get sono i getters di Vuex
        return this.cartToken;
    }

    get isBlackboxEnabled(): boolean {
        return store.state.options.blackbox.enabled;
    }

    get isEmpty(): boolean {
        return this.cart.items.length === 0;
    }

    get cartHasError(): boolean {
        return !this.cart.items.every((val: any) => {
            return val.extension_attributes.extitem_error === '';
        });
    }

    get isGiftcardCart(): boolean {
        return this.cart.items.length === 1 && this.cart.items[0].product_type === 'giftcard';
    }

    get shippingAssignementAddress(): QuoteAddress|void {
        if (this.cart.extension_attributes && this.cart.extension_attributes.shipping_assignments) {
            const assignements = this.cart.extension_attributes.shipping_assignments;
            if (Array.isArray(assignements)) {
                let found;
                assignements.forEach((assignement) => {
                    if (assignement.shipping && assignement.shipping.address) {
                        found = assignement.shipping.address;
                    }
                });
                if (found) {
                    return found;
                }
            }
        }
    }

    get shippingAssignement(): any|void {
        if (this.cart.extension_attributes && this.cart.extension_attributes.shipping_assignments) {
            const assignements = this.cart.extension_attributes.shipping_assignments;
            if (Array.isArray(assignements) && assignements.length > 0) {
                return assignements[0];
            }
        }
    }

    get hasGiftcard(): boolean {
        const giftSkus = store.state.options.gift.skus.split(',');
        return this.cart.items.some((item) => {
            return giftSkus.indexOf(item.sku) !== -1;
        });
    }

    get giftCardItems(): CartItem[] {
        const items: CartItem[] = [];
        const giftSkus = store.state.options.gift.skus.split(',');
        this.cart.items.forEach((item) => {
            if (giftSkus.indexOf(item.sku) !== -1) {
                items.push(item);
            }
        });
        return items;
    }

    get baseGrandTotal(): number {
        let grandTotal = this.totals.grand_total;
        if (this.totals.base_currency_code !== this.totals.quote_currency_code) {
            grandTotal = this.totals.base_grand_total;
        }
        return grandTotal;
    }

    get isCartGuestFlagSet(): boolean {
        return this.loadingFlags.guestlogin || this.loadingFlags.guestregister;
    }

    @Mutation // tutorial: le mutation di Vuex sono metodi pubblici con decorator @Mutation
    public setToken(token: string) {
        this.cartToken = token;
    }

    @Mutation
    public flagLoading(payload: {key: string, value: boolean}) {
        this.loadingFlags[payload.key] = payload.value;
    }

    @Mutation
    public setCart(cart: Cart) {
        this.cart = cart;
    }

    @Mutation
    public updateTotals(totals: Totals) {
        this.totals = totals;
    }

    @Mutation
    public setQty(payload: {itemId: number, qty: number}) {
        const item = this.cart.items.find((i) => i.item_id === payload.itemId);
        if (item) {
            item.qty = payload.qty;
        }
        let sum = 0;
        this.cart.items.forEach( (i: CartItem) => { sum += i.price * i.qty; });
        this.totals.subtotal = sum;
    }

    @Mutation
    public deleteItem(itemId: number) {
        const idx = this.cart.items.findIndex((i: CartItem) => i.item_id === itemId );
        this.cart.items.splice(idx, 1);

        let sum = 0;
        this.cart.items.forEach( (i: CartItem) => { sum += i.price * i.qty; });
        this.totals.subtotal = sum;
    }

    @Mutation
    public addItem(item: CartItem) {
        const idx = this.cart.items.findIndex((i: CartItem) => i.sku === item.sku );
        if (idx !== -1) {
            let existingItem = this.cart.items[idx];
            existingItem.qty++;
            let sum = 0;
            this.cart.items.forEach( (i: CartItem) => { sum += i.price * i.qty; });
            this.totals.subtotal = sum;
        } else {
            this.cart.items.push(item);
        }
    }

    @Mutation
    public reset() {
        this.cart = blankState('cart');
        this.totals = blankState('totals');
        this.cartToken = '';
        this.loadingFlags = loadingFlags;
    }

    @Action // tutorial: le action di Vuex sono metodo pubblici con decorator @Action
    public async getCart() {
        const cart: CartResponse = await getCart();
        this.setCart(cart);
        bus.$emit(receiveEvent, cart);
    }

    // tutorial: un metodo decorato con MutationAction è una action
    // il cui valore di return viene committato un una mutation
    @MutationAction({ mutate: ['totals'] })
    public async getTotals() {
        const m = this as any;
        m.commit('flagLoading', {key: 'totals', value: true});
        const totals: Totals = await getTotals();
        m.commit('flagLoading', {key: 'totals', value: false});
        return { totals };
    }

    @Action
    @debounce(500)
    public async updateQty(payload: {item: CartItem, oldQty: number}) {
        const data: UpdateCartRequest = {
            cartItem: {
                qty: payload.item.qty,
                quote_id: this.token.toString() !== '' ? this.token : this.cart.id,
            },
        };
        let err: any;
        let resp: any;

        this.flagLoading({key: 'totals', value: true});
        [ err, resp ] = await to(updateCartQty(data, payload.item.item_id));

        return new Promise((resolve, reject) => {
            if (err) {
                this.setQty({itemId: payload.item.item_id, qty: payload.oldQty});
                this.flagLoading({key: 'totals', value: false});
                reject(err);
                handleError(err, false);
            } else {
                if (this.isBlackboxEnabled) {
                    checkout.checkPromoOnTicket().finally(() => {
                        this.flagLoading({key: 'totals', value: false});
                    });
                } else {
                    checkout.collectCartInfo(['totals', 'shipping', 'payment', 'fidelity']).finally(() => {
                        this.flagLoading({key: 'totals', value: false});
                    });
                }
                resolve(resp);
            }
        });
    }

    @Action
    public async deleteCartItem(itemId: number) {
        // const cartItem = this.cart.items.find((i) => i.item_id === itemId);
        this.deleteItem(itemId);
        try {
            this.flagLoading({key: 'totals', value: true});
            await deleteCartItem(itemId);
        } catch (e) {
            handleError(e);
        }
        try {
            if (this.context.rootGetters.hasWarningsByType('blackbox')) {
                await checkout.checkPromoOnTicket();
            } else {
                if (this.isBlackboxEnabled) {
                    await checkout.checkPromoOnTicket();
                } else {
                    await checkout.collectCartInfo(['totals', 'shipping', 'payment', 'fidelity']);
                }
            }
        } catch (e) {
            // console.log(e);
        }

        this.flagLoading({key: 'totals', value: false});

        if (this.isEmpty) {
            this.context.commit('removeWarningByType', 'blackbox', { root: true });
        }
    }

    @Action({rawError: true})
    public async addCartItem(payload: {sku: string, sizeConfiguration: ConfigurableItemOption, qty?: number}) {
        const data: AddToCartRequest = {
            cartItem: {
                sku: payload.sku,
                qty: payload.qty ? payload.qty : 1,
                quote_id: this.token.toString() !== '' ? this.token : this.cart.id,
                product_option: {
                    extension_attributes: {
                        configurable_item_options: [payload.sizeConfiguration]
                    }
                }
            },
        };
        let err: any;
        let resp: CartItem|undefined;

        this.flagLoading({key: 'totals', value: true});
        [ err, resp ] = await to(addToCart(data));

        return new Promise((resolve, reject) => {
            if (err) {
                this.flagLoading({key: 'totals', value: false});
                reject(handleError(err, false, false));
            } else {
                this.addItem(resp as CartItem);
                checkout.collectCartInfo(['totals', 'shipping', 'payment', 'fidelity']).finally(() => {
                    this.flagLoading({key: 'totals', value: false});
                });
                resolve(resp);
            }
        });
    }

    @Action
    public async addToWishlist(itemId: number) {
        const item = this.cart.items.find((i) => i.item_id === itemId);
        if (item) {
            await addItemToWishlist({wishlistData: [{qty: item.qty, sku: item.sku}]});
            await this.deleteCartItem(itemId);
        }
    }
}

export default getModule(CartModule);
